import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { PageContainer } from "@/components/ui/page-container";
import { MobilePageTitle } from "@/components/ui/mobile-page-title";
import { Skeleton } from "@/components/ui/skeleton";
import { ResourceError } from "@/components/ui/resource-error";
import { Wizard } from "@/components/ui/wizard";
import { ConfirmDialog } from "@/components/ui/dialog/confirm-dialog";
import { toast } from "@/components/ui/toast";
import { useUnsavedChanges } from "@/hooks/use-unsaved-changes";
import { useAutoCollapsedSidebar } from "@/hooks/use-auto-collapsed-sidebar";
import { useGymChangeHandler } from "@/hooks/use-gym-change-handler";
import { useEntityNameBreadcrumb } from "@/hooks/use-breadcrumb/use-breadcrumb-overrides";
import { PROTECTED_ROUTES } from "@/utils/navigation";
import { getApiErrorMessages } from "@/utils/api-error";
import {
  StaffMemberCreationSchema,
  type StaffMemberCreationFormData,
} from "@/schemas/staff-member";
import { useStaffWizardSteps } from "@/components/create-staff-member/use-staff-wizard-steps";
import { staffMemberFormToUpdatePayload } from "@/components/create-staff-member/staff-member-payload";
import {
  mainAddressOf,
  staffMemberToFormValues,
} from "@/components/create-staff-member/staff-member-to-form-values";
import {
  useStaffMember,
  useUpdateStaffMember,
} from "@/queries/use-staff-members";
import { useRoles } from "@/queries/use-roles";
import type { StaffMember } from "@/api/staff-members/types";

export const Route = createFileRoute("/(protected)/staff/$staffMemberId/edit/")(
  {
    component: RouteComponent,
  },
);

function RouteComponent() {
  useAutoCollapsedSidebar();
  const { t } = useTranslation();
  const { staffMemberId } = Route.useParams();
  const {
    data: staffMember,
    isLoading,
    error,
  } = useStaffMember(Number(staffMemberId));
  const {
    data: rolesData,
    isLoading: rolesLoading,
    error: rolesError,
  } = useRoles();
  const loadError = error ?? rolesError;

  useEntityNameBreadcrumb(
    staffMember ? `${staffMember.firstName} ${staffMember.lastName}` : null,
  );

  // Role assignments only carry the role's type, so the roles list is needed
  // before the form can be prefilled.
  const roleIdByType = useMemo(
    () =>
      new Map(
        (rolesData?.staffRoles ?? []).map((role) => [
          role.roleType,
          role.id.toString(),
        ]),
      ),
    [rolesData],
  );

  return (
    <div className="flex h-full flex-col px-6 py-5">
      <MobilePageTitle>{t("pages:staff.wizard.editTitle")}</MobilePageTitle>
      <PageContainer
        className="flex min-h-0 min-w-[309px] flex-1 flex-col p-0 md:m-0 lg:mb-0"
        internalScroll
      >
        {loadError ? (
          <ResourceError
            error={loadError}
            notFoundMessage={t("pages:profile.basicData.errors.notFound")}
            errorMessage={t("pages:profile.basicData.errors.loadingFailed", {
              message: loadError.message,
            })}
            className="m-6"
          />
        ) : isLoading || rolesLoading || !staffMember ? (
          <div className="flex flex-col gap-4 p-8">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : (
          <EditStaffMemberWizard
            staffMember={staffMember}
            roleIdByType={roleIdByType}
          />
        )}
      </PageContainer>
    </div>
  );
}

// Mounted once the staff member has loaded, so the form starts from their
// values and a background refetch can't reset what's being edited.
function EditStaffMemberWizard({
  staffMember,
  roleIdByType,
}: {
  staffMember: StaffMember;
  roleIdByType: ReadonlyMap<string, string>;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutate: updateStaffMember, isPending } = useUpdateStaffMember();
  const steps = useStaffWizardSteps({
    currentAvatarUrl: staffMember.avatarUrl,
    emailDisabled: true,
    assignmentsDisabled: true,
  });

  const form = useForm<StaffMemberCreationFormData>({
    resolver: zodResolver(
      StaffMemberCreationSchema({ withAssignments: false }),
    ),
    mode: "onSubmit",
    defaultValues: staffMemberToFormValues(staffMember, roleIdByType),
  });

  const navigateBack = useCallback(
    () =>
      navigate({
        to: PROTECTED_ROUTES.STAFF_DETAIL,
        params: { staffMemberId: String(staffMember.id) },
        search: { tab: "basic-data", eventId: undefined, week: undefined },
      }),
    [navigate, staffMember.id],
  );

  const { handleCancelEdit, unsavedChangesDialogProps } = useUnsavedChanges({
    isEditing: true,
    isDirty: form.formState.isDirty,
  });

  useGymChangeHandler({
    hasUnsavedChanges: form.formState.isDirty,
    handleCancelEdit,
    navigateOnChange: PROTECTED_ROUTES.STAFF,
  });

  const handleComplete = (data: StaffMemberCreationFormData) => {
    updateStaffMember(
      {
        id: staffMember.id,
        data: staffMemberFormToUpdatePayload(data, {
          mainAddressId: mainAddressOf(staffMember)?.id,
        }),
      },
      {
        onSuccess: () => {
          toast.success(
            t("pages:profile.basicData.toaster.update.success.title"),
            {
              description: t(
                "pages:profile.basicData.toaster.update.success.description",
              ),
            },
          );
          navigateBack();
        },
        onError: (error) => {
          toast.error(t("pages:profile.basicData.toaster.update.error.title"), {
            description: getApiErrorMessages(error),
          });
        },
      },
    );
  };

  return (
    <>
      <Wizard
        form={form}
        steps={steps}
        onComplete={handleComplete}
        isSubmitting={isPending}
        onCancel={() => handleCancelEdit(navigateBack)}
      />
      <ConfirmDialog {...unsavedChangesDialogProps} />
    </>
  );
}
